import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Sidebar from '../Core/Sidebar';
import Navbar from '../Core/Navbar';

// Recursive component to render tree nodes
const TreeNode = ({ node, level = 0, onSelect, selectedId, expandedNodes, toggleNode }) => {
  const hasChildren = node.child && node.child.length > 0;
  const isSelected = selectedId === node.user_code;
  const isExpanded = expandedNodes[node.user_code] || level === 0; // Auto-expand root level
  
  const levelDisplay = level === 0 ? 'Root' : `L${level}`;

  const handleToggle = (e) => {
    e.stopPropagation();
    toggleNode(node.user_code);
  };

  const handleSelect = () => {
    onSelect(node);
  };

  // Format display name
  const displayName = `${node.first_name || ''} ${node.last_name || ''} [${node.user_code || 'No Code'}]`.trim();
  
  // Get account type/plan
  const accountType = node.package_id === 1 ? 'Basic' : node.package_id === 2 ? 'Premium' : 'Standard';

  return (
    <div className="tree-node">
      <div 
        className={`tree-item ${isSelected ? 'selected' : ''} ${!node.user_code ? 'inactive' : ''}`}
        style={{ paddingLeft: `${level * 20}px` }}
      >
        {hasChildren ? (
          <button 
            className="expand-btn"
            onClick={handleToggle}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="no-children-spacer">•</span>
        )}
         <span className={`level-badge level-${level}`}>
            {levelDisplay}
          </span>
        <span className="node-name" onClick={handleSelect}>
          <span className="node-status">
            {!node.user_code ? '⏸️ ' : ''}
          </span>
          <span className="node-name-text">{displayName}</span>
          <span className={`account-badge ${accountType.toLowerCase()}`}>
            {accountType}
          </span>
          {node.position && (
            <span className={`position-badge ${node.position.toLowerCase()}`}>
              {node.position}
            </span>
          )}
         
        </span>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="children">
          {node.child.map((child) => (
            <TreeNode
              key={child.user_code || Math.random().toString()}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UserDetails = ({ user }) => {
  if (!user) {
    return (
      <div className="user-details">
        <h2>User Details</h2>
        <p className="no-selection">Select a node to view details</p>
      </div>
    );
  }

  const isInactive = !user.user_code;
  const accountType = user.package_id === 1 ? 'Basic' : user.package_id === 2 ? 'Premium' : 'Standard';

  return (
    <div className="user-details">
      <h2>User Details</h2>
      <div className={`status-badge ${!user.user_code ? 'inactive' : ''}`}>
        {!user.user_code ? 'Inactive / Not Registered' : accountType}
      </div>
      
      <div className="detail-row">
        <label>User Code</label>
        <div className="detail-value">{user.user_code || 'Not Registered'}</div>
      </div>
      
      <div className="detail-row">
        <label>Full Name</label>
        <div className="detail-value">
          {user.first_name || ''} {user.last_name || ''}
          {!user.first_name && !user.last_name && 'N/A'}
        </div>
      </div>
      
      {/* <div className="detail-row">
        <label>Account Name</label>
        <div className="detail-value">{user.account_name || 'N/A'}</div>
      </div> */}
      
      <div className="detail-row">
        <label>Plan Type</label>
        <div className="detail-value">{accountType}</div>
      </div>
      
      {/* <div className="detail-row">
        <label>Position</label>
        <div className="detail-value">{user.position || 'N/A'}</div>
      </div> */}
      
      <div className="detail-row">
        <label>Sponsor Code</label>
        <div className="detail-value">{user.sponsor_code || 'N/A'}</div>
      </div>
      
      <div className="detail-row">
        <label>Active Date</label>
        <div className="detail-value">
          {user.active_date ? new Date(user.active_date).toLocaleString() : 'Not Active'}
        </div>
      </div>
    
    </div>
  );
};

export default function Genealogicaltree() {
  const authToken = useSelector((state) => state.user?.token);
  const userData = useSelector((state) => state.user?.userData);
  const [treeData, setTreeData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [searchError, setSearchError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
  
    // Check screen size on mount and resize
    useEffect(() => {
      const checkMobile = () => {
        const mobile = window.innerWidth < 992;
        setIsMobile(mobile);
  
        // Auto-close sidebar on mobile by default
        if (mobile) {
          setSidebarOpen(true);
        } else {
          setSidebarOpen(true);
        }
      };
  
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);
  
  
  
    const closeSidebar = () => {
      if (isMobile) {
        setSidebarOpen(false);
      }
    };

  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Initialize sidebar functionality
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar');
    const sidebarBody = document.querySelector('.sidebar-body');

    if (sidebar) {
      if (sidebarOpen) {
        sidebar.classList.remove('close');
      } else {
        sidebar.classList.add('close');
      }
    }

    if (sidebarBody) {
      sidebarBody.style.height = 'calc(100vh - 70px)';
      sidebarBody.style.overflowY = 'auto';
    }
  }, [sidebarOpen]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      /* Custom scrollbar styles - Global */
      * {
        scrollbar-width: thin;
        scrollbar-color: rgba(26, 155, 142, 0.5) rgba(20, 30, 50, 0.3);
      }
      
      /* Webkit scrollbar styles */
      ::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      
      ::-webkit-scrollbar-track {
        background: rgba(20, 30, 50, 0.3);
        border-radius: 10px;
      }
      
      ::-webkit-scrollbar-thumb {
        background: rgba(26, 155, 142, 0.5);
        border-radius: 10px;
        transition: background 0.3s ease;
      }
      
      ::-webkit-scrollbar-thumb:hover {
        background: rgba(26, 155, 142, 0.8);
      }
      
      /* Sidebar scrollbar */
      .sidebar-body {
        height: calc(100vh - 70px);
        overflow-y: auto;
        overflow-x: hidden;
      }
      
      .sidebar-body::-webkit-scrollbar {
        width: 4px;
      }
      
      .sidebar-body::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
      }
      
      .sidebar-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
      }
      
      .tree-box {
        border: 2px solid #2a3f5f;
        border-radius: 8px;
        padding: 20px;
        background: rgba(20, 30, 50, 0.5);
        min-height: 200px;
        max-height: 550px;
        overflow-y: scroll !important;
        overflow-x: auto !important;
        position: relative;
        -webkit-overflow-scrolling: touch;
      }

      /* Force scrollbar to be visible */
      .tree-box::-webkit-scrollbar {
        width: 8px !important;
        height: 8px !important;
        display: block !important;
        background: transparent;
      }

      .tree-box::-webkit-scrollbar-track {
        background: rgba(20, 30, 50, 0.5) !important;
        border-radius: 4px !important;
      }

      .tree-box::-webkit-scrollbar-thumb {
        background: #2a3f5f !important;
        border-radius: 4px !important;
        min-height: 40px;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }

      .tree-box::-webkit-scrollbar-thumb:hover {
        background: #1a9b8e !important;
      }

      /* Firefox support */
      .tree-box {
        scrollbar-width: thin !important;
        scrollbar-color: #2a3f5f rgba(20, 30, 50, 0.5) !important;
      }

      /* Mobile scrollbar adjustments */
      @media (max-width: 768px) {
        ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        
        .tree-box {
          min-height: 550px;
        }
        
        .sidebar-body {
          height: calc(100vh - 60px);
        }
      }
      
      @media (max-width: 350px) {
        ::-webkit-scrollbar {
          width: 3px;
          height: 3px;
        }
        
        .tree-box {
          max-height: 350px;
        }
      }
      
      /* Ensure scrollable containers have proper touch scrolling */
      .sidebar-body,
      .tree-box,
      .user-details,
      .page-content {
        -webkit-overflow-scrolling: touch;
      }
      
      /* Prevent content from being hidden under fixed elements */
      .page-wrapper {
        position: relative;
        min-height: 100vh;
        overflow-x: hidden;
      }
      
      .page-content {
        overflow-x: hidden;
        overflow-y: auto;
        min-height: calc(100vh - 70px);
        padding: 20px;
      }
      
      @media (max-width: 768px) {
        .page-content {
          padding: 15px;
        }
      }
      
      @media (max-width: 350px) {
        .page-content {
          padding: 10px;
        }
      }
      
      /* Modal scrollbar */
      .modal-body {
        max-height: 70vh;
        overflow-y: auto;
      }
      
      /* Smooth transitions */
      .sidebar {
        transition: all 0.3s ease;
      }
      
      .modal {
        transition: opacity 0.3s ease;
      }
      
      /* Sidebar dropdown menu styles */
      .sidebar .nav-dropdown {
        list-style: none;
        padding-left: 0;
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease;
      }
      
      .sidebar .nav-dropdown.show {
        max-height: 500px;
      }
      
      .sidebar .nav-dropdown-item {
        padding-left: 2.5rem;
      }
      
      .nav-item.has-dropdown .nav-link {
        position: relative;
        padding-right: 2.5rem;
      }
      
      .nav-item.has-dropdown .dropdown-icon {
        position: absolute;
        right: 1rem;
        top: 50%;
        transform: translateY(-50%);
        transition: transform 0.3s ease;
      }
      
      .nav-item.has-dropdown .nav-link[aria-expanded="true"] .dropdown-icon {
        transform: translateY(-50%) rotate(180deg);
      }
      
      .nav-item.has-dropdown .nav-dropdown {
        background: rgba(255, 255, 255, 0.05);
        border-left: 3px solid var(--primary-color, #0d6efd);
        margin-left: 1rem;
        border-radius: 0 0 8px 8px;
      }
      
      .nav-item.has-dropdown .nav-dropdown-item .nav-link {
        padding: 0.75rem 1rem 0.75rem 2rem;
        color: rgba(255, 255, 255, 0.8);
      }
      
      .nav-item.has-dropdown .nav-dropdown-item .nav-link:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
      
      /* Custom card styling */
      .custom-card {
        position: relative;
        border-radius: 10px;
        overflow: hidden;
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
        border: 1px solid #e9ecef;
        transition: all 0.3s ease;
      }
      
      .custom-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0,0,0,0.1);
      }
      
      .custom-card .top-left,
      .custom-card .top-right,
      .custom-card .bottom-left,
      .custom-card .bottom-right {
        position: absolute;
        width: 20px;
        height: 20px;
        border-color: var(--primary-color, #0d6efd);
      }
      
      .custom-card .top-left {
        top: 0;
        left: 0;
        border-top: 2px solid;
        border-left: 2px solid;
      }
      
      .custom-card .top-right {
        top: 0;
        right: 0;
        border-top: 2px solid;
        border-right: 2px solid;
      }
      
      .custom-card .bottom-left {
        bottom: 0;
        left: 0;
        border-bottom: 2px solid;
        border-left: 2px solid;
      }
      
      .custom-card .bottom-right {
        bottom: 0;
        right: 0;
        border-bottom: 2px solid;
        border-right: 2px solid;
      }
      
      /* Notification styles */
      .notification {
        animation: slideIn 0.3s ease-out;
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        min-width: 300px;
      }
      
      @media (max-width: 768px) {
        .notification {
          min-width: calc(100% - 40px);
          left: 20px;
          right: 20px;
          top: 10px;
        }
      }
      
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  console.log("userData from Redux: ", userData);

  // Toggle node expansion
  const toggleNode = useCallback((userCode) => {
    setExpandedNodes(prev => ({
      ...prev,
      [userCode]: !prev[userCode]
    }));
  }, []);

  // Expand all nodes
  const expandAll = useCallback((node) => {
    const expandNode = (node) => {
      let expanded = { [node.user_code]: true };
      if (node.child && node.child.length > 0) {
        node.child.forEach(child => {
          expanded = { ...expanded, ...expandNode(child) };
        });
      }
      return expanded;
    };

    if (treeData) {
      const allExpanded = expandNode(treeData);
      setExpandedNodes(allExpanded);
    }
  }, [treeData]);

  // Collapse all nodes except root
  const collapseAll = useCallback(() => {
    if (treeData) {
      setExpandedNodes({ [treeData.user_code]: true });
    }
  }, [treeData]);

  // Search function to find a node in the tree
  const searchInTree = (node, searchParams) => {
    if (!node) return null;

    const { type, value } = searchParams;
    let matches = false;

    switch (type) {
      case 'user_code':
        matches = node.user_code && node.user_code.toLowerCase().includes(value.toLowerCase());
        break;
      case 'name':
        const fullName = `${node.first_name || ''} ${node.last_name || ''}`.toLowerCase();
        matches = fullName.includes(value.toLowerCase());
        break;
      case 'account_name':
        matches = node.account_name && node.account_name.toLowerCase().includes(value.toLowerCase());
        break;
      case 'sponsor_code':
        matches = node.sponsor_code && node.sponsor_code.toLowerCase().includes(value.toLowerCase());
        break;
      default:
        matches = false;
    }

    if (matches) {
      return node;
    }

    if (node.child && node.child.length > 0) {
      for (const child of node.child) {
        const found = searchInTree(child, searchParams);
        if (found) return found;
      }
    }

    return null;
  };

  // Handle search
  const handleSearch = async (searchParams) => {
    if (!treeData) return;

    setSearchLoading(true);
    setSearchError(null);

    // Simulate search delay (remove in production)
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const foundNode = searchInTree(treeData, searchParams);

      if (foundNode) {
        // Expand all parent nodes to show the found node
        const expandPath = (node, targetCode) => {
          if (node.user_code === targetCode) return true;
          
          if (node.child && node.child.length > 0) {
            for (const child of node.child) {
              if (expandPath(child, targetCode)) {
                setExpandedNodes(prev => ({
                  ...prev,
                  [node.user_code]: true
                }));
                return true;
              }
            }
          }
          return false;
        };

        setExpandedNodes({}); // Reset expansions
        expandPath(treeData, foundNode.user_code);
        setSelectedNode(foundNode);
        
        // Scroll to the found node (you might need to implement this with refs)
        setTimeout(() => {
          const selectedElement = document.querySelector('.tree-item.selected');
          if (selectedElement) {
            selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        setSearchError(`No user found with this ${searchParams.type.replace('_', ' ')}`);
      }
    } catch (err) {
      setSearchError('An error occurred during search');
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle "Go to My view" button
  const handleGoToMyView = () => {
    if (treeData) {
      setSelectedNode(treeData);
      setExpandedNodes({ [treeData.user_code]: true });
      
      // Scroll to root
      setTimeout(() => {
        const rootElement = document.querySelector('.tree-item');
        if (rootElement) {
          rootElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Fetch genealogical tree data
  const fetchGenealogicalTree = useCallback(async () => {
    if (!authToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      const userCode = userData?.user_code || 'WQ13263952';
      formData.append('user_code', userCode);

      console.log('Fetching tree for user_code:', userCode);

      const response = await fetch('https://global.thewillowquantum.com/api/user/get-direct-full-by-usercode', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log('API Response:', result);

      if (result.status && result.data) {
        setTreeData(result.data);
        setExpandedNodes({ [result.data.user_code]: true });
        setSelectedNode(result.data);
      } else {
        throw new Error('Invalid API response structure');
      }
      
    } catch (err) {
      console.error('Error fetching genealogical tree:', err);
      setError(err.message || 'Failed to fetch genealogical tree');
      
      if (userData) {
        const fallbackNode = {
          user_code: userData.user_code,
          first_name: userData.name?.split(' ')[0] || 'User',
          last_name: userData.name?.split(' ')[1] || '',
          account_name: userData.account_name,
          package_id: userData.package_id,
          position: userData.position,
          sponsor_code: userData.sponsor_code,
          active_date: userData.active_date,
          child: []
        };
        setTreeData(fallbackNode);
        setExpandedNodes({ [fallbackNode.user_code]: true });
        setSelectedNode(fallbackNode);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, userData]);

  // Fetch data when component mounts
  useEffect(() => {
    fetchGenealogicalTree();
  }, [fetchGenealogicalTree]);

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
  };

  const handleRetry = () => {
    fetchGenealogicalTree();
  };

  if (loading) {
    return (
      <div className="genealogical-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading genealogical tree...</p>
        </div>
        <style jsx>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
            gap: 20px;
            padding: 20px;
            text-align: center;
          }
          
          .spinner {
            border: 4px solid rgba(255, 255, 255, 0.1);
            border-radius: 50%;
            border-top: 4px solid #1a9b8e;
            width: 50px;
            height: 50px;
            animation: spin 1s linear infinite;
          }
          
          @media (max-width: 350px) {
            .spinner {
              width: 40px;
              height: 40px;
              border-width: 3px;
            }
            
            .loading-container p {
              font-size: 14px;
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="main-wrapper dark-theme">
      {/* Sidebar */}
      <nav className={`sidebar ${sidebarOpen ? '' : 'close'} ${isMobileView ? 'mobile' : ''}`}>
        <div className="sidebar-header">
          <a href="#" className="sidebar-brand">
            <i className="me-2" data-lucide="atom"></i>
            Willow <span>Quantum</span>
          </a>
          <div className="sidebar-toggler" onClick={toggleSidebar}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
       <Sidebar
                       isOpen={!sidebarOpen}
                       onClose={closeSidebar}
                     />
           
                     {/* Mobile backdrop */}
                     {isMobile && sidebarOpen && (
                       <div
                         className="sidebar-backdrop"
                         onClick={closeSidebar}
                       />
                     )}
          </nav>
    
          <div className="page-wrapper">
            <Navbar toggleSidebar={toggleSidebar}  isSidebarOpen={sidebarOpen}
                isMobile={isMobile}/>
    
    

        <div className="page-content">
          <div className="genealogical-container">
            {/* Search Section */}
            {/* <SearchSection 
              onSearch={handleSearch} 
              searchLoading={searchLoading}
            />
            
      
            {searchError && (
              <div className="search-error-message">
                <p>{searchError}</p>
              </div>
            )} */}
            
            <div className="tree-section">
              <div className="tree-header">
                <h1>Genealogical Tree</h1>
                {treeData && (
                  <div className="tree-controls">
                    <button onClick={expandAll} className="control-btn">
                      Expand All
                    </button>
                    <button onClick={collapseAll} className="control-btn">
                      Collapse All
                    </button>
                  </div>
                )}
              </div>
              
              {error && (
                <div className="error-message">
                  <p>Error: {error}</p>
                  <button onClick={handleRetry} className="retry-btn">
                    Retry
                  </button>
                </div>
              )}
              
              <div className="tree-box">
                {treeData ? (
                  <TreeNode
                    node={treeData}
                    level={0}
                    onSelect={handleNodeSelect}
                    selectedId={selectedNode?.user_code}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                  />
                ) : (
                  <p className="no-data">No genealogical data available</p>
                )}
              </div>
            </div>
            
            <UserDetails user={selectedNode} />
          </div>
        </div>
      </div>

      <style jsx>{`
        .genealogical-container {
          min-height: 100vh;
          background: #0a0e27;
          color: #ffffff;
          padding: 40px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Search Section Styles */
        .search-section {
          margin-bottom: 32px;
          background: rgba(20, 30, 50, 0.5);
          border: 2px solid #2a3f5f;
          border-radius: 8px;
          padding: 20px;
        }

        .search-container {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-type-selector {
          min-width: 140px;
        }

        .search-select {
          width: 100%;
          padding: 10px 14px;
          background: rgba(10, 14, 39, 0.8);
          border: 1px solid #2a3f5f;
          border-radius: 6px;
          color: #ffffff;
          font-size: 14px;
          cursor: pointer;
          outline: none;
          transition: all 0.2s ease;
        }

        .search-select:hover {
          border-color: #1a9b8e;
        }

        .search-select option {
          background: #0a0e27;
          color: #ffffff;
        }

        .search-input-wrapper {
          flex: 1;
          min-width: 250px;
        }

        .search-input {
          width: 100%;
          padding: 10px 14px;
          background: rgba(10, 14, 39, 0.8);
          border: 1px solid #2a3f5f;
          border-radius: 6px;
          color: #ffffff;
          font-size: 14px;
          outline: none;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          border-color: #1a9b8e;
          box-shadow: 0 0 0 2px rgba(26, 155, 142, 0.2);
        }

        .search-input::placeholder {
          color: #8b9dc3;
          opacity: 0.7;
        }

        .search-button,
        .my-view-button {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .search-button {
          background: #1a9b8e;
          color: #ffffff;
          min-width: 120px;
        }

        .search-button:hover:not(:disabled) {
          background: #148075;
          transform: translateY(-1px);
        }

        .search-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .my-view-button {
          background: transparent;
          border: 1px solid #1a9b8e;
          color: #1a9b8e;
        }

        .my-view-button:hover {
          background: rgba(26, 155, 142, 0.1);
          transform: translateY(-1px);
        }

        .search-error-message {
          background: rgba(255, 68, 68, 0.1);
          border: 1px solid #ff4444;
          border-radius: 6px;
          padding: 10px 16px;
          margin-bottom: 24px;
        }

        .search-error-message p {
          color: #ff8888;
          margin: 0;
          font-size: 13px;
        }

        .tree-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        h1 {
          font-size: 28px;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
        }

        h2 {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 20px;
          color: #ffffff;
        }

        .tree-controls {
          display: flex;
          gap: 12px;
        }

        .control-btn {
          background: rgba(26, 155, 142, 0.2);
          border: 1px solid #1a9b8e;
          color: #ffffff;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .control-btn:hover {
          background: #1a9b8e;
        }

        .tree-section {
          margin-bottom: 48px;
        }

        .tree-box {
          border: 2px solid #2a3f5f;
          border-radius: 8px;
          padding: 20px;
          background: rgba(20, 30, 50, 0.5);
          min-height: 200px;
          max-height: 550px;
          overflow-y: scroll !important;
          overflow-x: auto;
        }

        .tree-node {
          margin: 2px 0;
        }

        .tree-item {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
          position: relative;
        }

        .tree-item:hover {
          background: rgba(45, 60, 90, 0.6);
        }

        .tree-item.selected {
          background: #1a9b8e;
        }

        .tree-item.inactive {
          opacity: 0.7;
          background: rgba(255, 68, 68, 0.1);
        }

        .expand-btn {
          background: transparent;
          border: none;
          color: #ffffff;
          cursor: pointer;
          font-size: 12px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;
          padding: 0;
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }

        .expand-btn:hover {
          transform: scale(1.2);
        }

        .no-children-spacer {
          width: 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;
          color: #8b9dc3;
          font-size: 12px;
          flex-shrink: 0;
        }

        .node-name {
          font-size: 14px;
          line-height: 1.5;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          flex: 1;
        }

        .node-name-text {
          word-break: break-word;
        }

        .node-status {
          color: #ffaa66;
        }

        .account-badge,
        .position-badge {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 12px;
          white-space: nowrap;
        }

        .account-badge {
          background: rgba(255, 255, 255, 0.1);
        }

        .account-badge.basic {
          background: #6c757d;
        }

        .account-badge.premium {
          background: #ffc107;
          color: #000;
        }

        .account-badge.standard {
          background: #17a2b8;
        }

        .position-badge {
          background: rgba(255, 255, 255, 0.1);
        }

        .position-badge.left {
          background: #28a745;
        }

        .position-badge.right {
          background: #dc3545;
        }

        .children {
          margin-left: 0;
        }

        .user-details {
          border: 2px solid #2a3f5f;
          border-radius: 8px;
          padding: 24px;
          background: rgba(20, 30, 50, 0.5);
          max-width: 550px;
        }

        .status-badge {
          display: inline-block;
          background: #1a9b8e;
          color: #ffffff;
          padding: 6px 16px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 24px;
        }

        .status-badge.inactive {
          background: #6c757d;
        }

        .detail-row {
          margin-bottom: 20px;
        }

        .detail-row label {
          display: block;
          font-size: 13px;
          color: #8b9dc3;
          margin-bottom: 8px;
        }

        .detail-value {
          background: transparent;
          border-bottom: 1px solid #2a3f5f;
          padding: 10px 0;
          font-size: 15px;
          color: #ffffff;
          word-break: break-word;
        }

        .no-selection {
          color: #8b9dc3;
          font-size: 14px;
          text-align: center;
          padding: 40px 0;
        }

        .error-message {
          background: rgba(255, 68, 68, 0.1);
          border: 1px solid #ff4444;
          border-radius: 8px;
          padding: 16px 20px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
        }

        .error-message p {
          color: #ff8888;
          margin: 0;
          font-size: 14px;
          word-break: break-word;
        }

        .retry-btn {
          background: transparent;
          border: 1px solid #ff8888;
          color: #ff8888;
          padding: 8px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .retry-btn:hover {
          background: rgba(255, 136, 136, 0.1);
        }

        .no-data {
          color: #8b9dc3;
          font-size: 14px;
          text-align: center;
          padding: 40px 0;
          margin: 0;
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .genealogical-container {
            padding: 20px;
          }

          .search-section {
            padding: 16px;
          }

          .search-container {
            flex-direction: column;
            align-items: stretch;
          }

          .search-type-selector,
          .search-input-wrapper,
          .search-button,
          .my-view-button {
            width: 100%;
          }

          .search-type-selector {
            min-width: auto;
          }

          .search-button,
          .my-view-button {
            padding: 10px;
          }

          .tree-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          h1 {
            font-size: 24px;
          }

          .tree-controls {
            width: 100%;
            justify-content: flex-start;
          }

          .control-btn {
            padding: 6px 14px;
            font-size: 12px;
          }

          .tree-box {
            padding: 16px;
            max-height: 450px;
          }

          .user-details {
            padding: 20px;
            max-width: 100%;
          }

          .detail-value {
            font-size: 14px;
          }
        }

        @media (max-width: 576px) {
          .genealogical-container {
            padding: 16px;
          }

          h1 {
            font-size: 22px;
          }

          h2 {
            font-size: 18px;
          }

          .search-section {
            padding: 14px;
          }

          .search-select,
          .search-input,
          .search-button,
          .my-view-button {
            font-size: 13px;
            padding: 8px 12px;
          }

          .tree-box {
            padding: 12px;
            min-height: 550px;
          }

          .tree-item {
            padding: 6px 10px;
          }

          .node-name {
            font-size: 13px;
            gap: 6px;
          }

          .account-badge,
          .position-badge {
            font-size: 10px;
            padding: 2px 6px;
          }

          .user-details {
            padding: 16px;
          }

          .status-badge {
            font-size: 12px;
            padding: 5px 12px;
          }

          .detail-row {
            margin-bottom: 16px;
          }

          .detail-row label {
            font-size: 12px;
          }

          .detail-value {
            font-size: 13px;
            padding: 8px 0;
          }
        }

        @media (max-width: 350px) {
          .genealogical-container {
            padding: 12px;
          }

          h1 {
            font-size: 20px;
          }

          h2 {
            font-size: 17px;
            margin-bottom: 16px;
          }

          .search-section {
            padding: 12px;
          }

          .search-select,
          .search-input,
          .search-button,
          .my-view-button {
            font-size: 12px;
            padding: 7px 10px;
          }

          .tree-header {
            gap: 12px;
          }

          .tree-controls {
            gap: 8px;
          }

          .control-btn {
            padding: 5px 10px;
            font-size: 11px;
            flex: 1;
          }

          .tree-box {
            padding: 10px;
            max-height: 350px;
            border-width: 1px;
          }

          .tree-item {
            padding: 5px 8px;
          }

          .expand-btn,
          .no-children-spacer {
            width: 20px;
            height: 20px;
            margin-right: 6px;
            font-size: 10px;
          }

          .node-name {
            font-size: 12px;
            gap: 4px;
          }

          .account-badge,
          .position-badge {
            font-size: 9px;
            padding: 1px 5px;
            border-radius: 10px;
          }

          .user-details {
            padding: 14px;
            border-width: 1px;
          }

          .status-badge {
            font-size: 11px;
            padding: 4px 10px;
            margin-bottom: 20px;
          }

          .detail-row {
            margin-bottom: 14px;
          }

          .detail-row label {
            font-size: 11px;
            margin-bottom: 6px;
          }

          .detail-value {
            font-size: 12px;
            padding: 6px 0;
          }

          .error-message {
            padding: 12px 16px;
          }

          .error-message p {
            font-size: 12px;
          }

          .retry-btn {
            padding: 6px 16px;
            font-size: 11px;
          }

          .search-error-message {
            padding: 8px 12px;
          }

          .search-error-message p {
            font-size: 11px;
          }

          .no-selection,
          .no-data {
            font-size: 12px;
            padding: 30px 0;
          }
        }

        /* Ensure scrollbars are always visible on mobile */
        @media (max-width: 768px) {
          .tree-box {
            -webkit-overflow-scrolling: touch;
            overflow-y: scroll !important;
          }

          .tree-box::-webkit-scrollbar {
            width: 6px;
            height: 6px;
            display: block !important;
          }
        }

        /* Force scrollbar visibility for all browsers */
        * {
          scrollbar-width: thin;
          scrollbar-color: #2a3f5f rgba(20, 30, 50, 0.5);
        }

        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
          display: block !important;
          -webkit-appearance: none;
        }

        ::-webkit-scrollbar-track {
          background: rgba(20, 30, 50, 0.5);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb {
          background: #2a3f5f;
          border-radius: 4px;
          min-height: 40px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #1a9b8e;
        }

        .tree-box,
        .sidebar-body,
        .modal-body,
        .page-content {
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .tree-box {
          scrollbar-width: thin;
          scrollbar-color: #2a3f5f rgba(20, 30, 50, 0.5);
        }
      `}</style>
    </div>
  );
}